/**
 * API surface for the AI Skills admin page. The HTTP helpers live in
 * utils/api.js (aiSkills block); this module adds the org/location lookups
 * the page needs to show names instead of ids.
 *
 * Backend: azfApi/fastify/routes/ai/skills.js (admin-only).
 */
import {
  listAiSkills,
  getAiSkill,
  createAiSkill,
  updateAiSkill,
  setAiSkillStatus,
  deleteAiSkill,
  listAiSkillReflections,
  runAiSkillReflection,
} from '../../../utils/api';
import { getOrganizations } from '../../../utils/organizationApi';
import { getLocations } from '../../../utils/locationApi';

export {
  listAiSkills,
  getAiSkill,
  createAiSkill,
  updateAiSkill,
  setAiSkillStatus,
  deleteAiSkill,
  listAiSkillReflections,
  runAiSkillReflection,
};

/**
 * { orgs: Map<id, name>, locations: Map<id, { name, orgId }>, orgOptions, locationOptions }
 * Failures resolve to empty lookups — names are a nicety, the page must still work.
 */
export async function loadScopeLookups() {
  const [orgsRaw, locsRaw] = await Promise.all([
    getOrganizations().catch(() => []),
    getLocations().catch(() => []),
  ]);
  const orgList = (Array.isArray(orgsRaw) ? orgsRaw : [])
    .filter((o) => o && o.id != null && !o.isDeleted)
    .map((o) => ({ id: Number(o.id), name: o.name || o.code || `Organization #${o.id}`, code: o.code || '' }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  const orgs = new Map(orgList.map((o) => [o.id, o.name]));
  const locList = (Array.isArray(locsRaw) ? locsRaw : [])
    .filter((l) => l && l.id != null && !l.isDeleted)
    .map((l) => ({ id: Number(l.id), name: l.name || l.code || `Property #${l.id}`, orgId: l.orgId != null ? Number(l.orgId) : null }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  const locations = new Map(locList.map((l) => [l.id, { name: l.name, orgId: l.orgId }]));
  return {
    orgs,
    locations,
    orgOptions: orgList.map((o) => ({ value: o.id, label: o.name, hint: o.code })),
    locationOptions: locList.map((l) => ({ value: l.id, label: l.name, hint: orgs.get(l.orgId) || '', orgId: l.orgId })),
  };
}
